import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from './AuthContext';
import ApiUrl from '../../utils/config';
import axios from 'axios';
import { RegisterModel, RoleClient } from '../../utils/apiClient';
import { FormPage, Field, ChipGroup, PasswordInput, PersonFields, CheckIcon } from '../../common/components/FormKit';
import PasswordRequirements from './PasswordRequirements';
import { validatePassword } from '../../utils/passwordPolicy';
import { parseApiError } from '../../utils/apiError';
import {
  confirmRegistration,
  resendRegistrationCode,
  REGISTRATION_CODE_LENGTH,
  REGISTRATION_RESEND_SECONDS,
} from '../../utils/registrationApi';

const MINOR_AGE = 18;

const isSameDay = (a: Date, b: Date) =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

const calcAge = (birth: Date, now: Date = new Date()): number => {
  let age = now.getFullYear() - birth.getFullYear();
  const beforeBirthday = now.getMonth() < birth.getMonth()
    || (now.getMonth() === birth.getMonth() && now.getDate() < birth.getDate());
  if (beforeBirthday) age -= 1;
  return age;
};

const Register: React.FC = () => {
  const [formData, setFormData] = useState<RegisterModel>(new RegisterModel({
    lastName: '',
    firstName: '',
    patronymic: '',
    birthDate: new Date(),
    city: '',
    email: '',
    password: '',
    roleIds: [] as number[]
  }));
  const [consent, setConsent] = useState(false);
  const [guardianConsent, setGuardianConsent] = useState(false);
  const [consentError, setConsentError] = useState('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const [roles, setRoles] = useState<any[]>([]);
  const [rolesLoading, setRolesLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [roleError, setRoleError] = useState('');
  // Шаг 1 — анкета, шаг 2 — ввод кода из письма
  const [step, setStep] = useState<'form' | 'code'>('form');
  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState('');
  const [codeInfo, setCodeInfo] = useState('');
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendIn, setResendIn] = useState(0);
  const emailInputRef = useRef<HTMLInputElement>(null);
  const codeInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const { register } = useAuth();
  const roleClient = new RoleClient(ApiUrl, axios);

  // Роль «Тренер» доступна только по закрытой ссылке-приглашению: /register?coach=КОД
  const [searchParams] = useSearchParams();
  const coachCode = searchParams.get('coach') ?? '';
  const [coachInviteValid, setCoachInviteValid] = useState(false);

  useEffect(() => {
    if (!coachCode) {
      setCoachInviteValid(false);
      return;
    }
    let cancelled = false;
    axios.get<{ valid: boolean }>(`${ApiUrl}/api/Auth/coach-invite`, { params: { code: coachCode } })
      .then((response) => { if (!cancelled) setCoachInviteValid(Boolean(response.data?.valid)); })
      .catch(() => { if (!cancelled) setCoachInviteValid(false); });
    return () => { cancelled = true; };
  }, [coachCode]);

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const response = await roleClient.getRoles();
        // При самостоятельной регистрации доступны только эти роли (сервер тоже отбрасывает остальные).
        // Судья, администратор и т. д. назначаются только администратором.
        setRoles(response.filter(role => role.name === 'Спортсмен' || role.name === 'Тренер' || role.name === 'Представитель'));
      } catch (error) {
        console.error('Error fetching roles:', error);
      } finally {
        setRolesLoading(false);
      }
    };

    fetchRoles();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setConsentError('');
    setRoleError('');

    if (birthDateUnset) {
      setError('Укажите дату рождения.');
      return;
    }

    if (!(formData.roleIds?.length)) {
      setRoleError('Выберите хотя бы одну роль: спортсмен, тренер или родитель.');
      setError('Выберите роль.');
      return;
    }

    if (!formData.gender) {
      setError('Укажите пол: он нужен для распределения по категориям на турнирах.');
      return;
    }

    // Validate password
    if (formData.password !== confirmPassword) {
      setError('Пароли не совпадают');
      return;
    }

    const passwordError = validatePassword(formData.password, formData.email);
    if (passwordError) {
      setError(passwordError);
      return;
    }

    if (!consent) {
      setConsentError('Для регистрации необходимо согласие на обработку персональных данных.');
      return;
    }
    if (isMinor && !guardianConsent) {
      setConsentError('Для участников младше 18 лет нужно подтвердить согласие родителя или законного представителя.');
      return;
    }

    setSubmitting(true);
    try {
      // Сервер не создаёт аккаунт сразу: он отправляет на почту код, а аккаунт появится после подтверждения
      await register(new RegisterModel({
        ...formData,
        email: formData.email.trim(),
        personalDataConsent: consent,
        guardianConsent: isMinor ? guardianConsent : false,
        coachInviteCode: coachInviteValid ? coachCode : undefined,
      }));
      setCode('');
      setCodeError('');
      setCodeInfo('');
      setResendIn(REGISTRATION_RESEND_SECONDS);
      setStep('code');
    } catch (err: any) {
      const apiError = parseApiError(err);
      if (apiError.field === 'email' && apiError.message) {
        // Например, такая почта уже есть в базе — показываем ошибку прямо у поля
        setEmailError(apiError.message);
        emailInputRef.current?.focus();
      } else if (apiError.field === 'roles' && apiError.message) {
        setRoleError(apiError.message);
      } else if (apiError.field === 'consent' && apiError.message) {
        setConsentError(apiError.message);
      } else {
        setError(apiError.message || 'Ошибка регистрации. Попробуйте еще раз.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Обратный отсчёт до возможности запросить код повторно
  useEffect(() => {
    if (resendIn <= 0) return undefined;
    const timer = window.setTimeout(() => setResendIn((s) => s - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [resendIn]);

  useEffect(() => {
    if (step === 'code') codeInputRef.current?.focus();
  }, [step]);

  // Вернуться к анкете (например, чтобы исправить адрес): введённые данные сохраняются
  const backToForm = (message = '') => {
    setStep('form');
    setError(message);
    setCode('');
    setCodeError('');
    setCodeInfo('');
  };

  const handleConfirm = async (e: React.FormEvent) => {
    e.preventDefault();
    if (verifying) return;
    if (code.length !== REGISTRATION_CODE_LENGTH) {
      setCodeError(`Введите ${REGISTRATION_CODE_LENGTH}-значный код из письма`);
      return;
    }

    setCodeError('');
    setCodeInfo('');
    setVerifying(true);
    try {
      await confirmRegistration(formData.email.trim(), code);
      navigate('/login', { state: { registered: true } });
    } catch (err: any) {
      const apiError = parseApiError(err);
      if (apiError.field === 'email' && apiError.message) {
        backToForm();
        setEmailError(apiError.message);
      } else if (apiError.expired) {
        backToForm(apiError.message || 'Заявка устарела. Заполните форму заново.');
      } else {
        setCodeError(apiError.message || 'Не удалось подтвердить код. Попробуйте ещё раз.');
        setCode('');
        codeInputRef.current?.focus();
      }
      setVerifying(false);
    }
  };

  const handleResend = async () => {
    if (resending || resendIn > 0) return;
    setCodeError('');
    setCodeInfo('');
    setResending(true);
    try {
      await resendRegistrationCode(formData.email.trim());
      setCode('');
      setCodeInfo('Новый код отправлен. Предыдущий больше не действует.');
      setResendIn(REGISTRATION_RESEND_SECONDS);
      codeInputRef.current?.focus();
    } catch (err: any) {
      const apiError = parseApiError(err);
      if (apiError.expired) {
        backToForm(apiError.message || 'Заявка устарела. Заполните форму заново.');
      } else {
        setCodeError(apiError.message || 'Не удалось отправить код. Попробуйте позже.');
      }
    } finally {
      setResending(false);
    }
  };

  const birthDate = formData.birthDate ? new Date(formData.birthDate) : new Date();
  // Пока дата рождения равна сегодняшней, считаем, что пользователь её ещё не указал
  const birthDateUnset = isSameDay(birthDate, new Date());
  const isMinor = !birthDateUnset && calcAge(birthDate) < MINOR_AGE;
  const passwordMismatch = confirmPassword.length > 0 && confirmPassword !== formData.password;

  return (
    <FormPage
      title="Регистрация"
      subtitle={step === 'form'
        ? 'Создайте аккаунт, чтобы участвовать в турнирах JUDO40'
        : 'Остался один шаг — подтвердите адрес электронной почты'}
    >

      {step === 'form' && (
      <form className="form-page__form" onSubmit={handleSubmit}>
        {error && <div className="form-page__alert" role="alert">{error}</div>}

        {/* ФИО, дата рождения, город, email, пол */}
        <PersonFields
          idPrefix="reg"
          value={{
            lastName: formData.lastName,
            firstName: formData.firstName,
            patronymic: formData.patronymic,
            birthDate: formData.birthDate,
            city: formData.city,
            gender: formData.gender,
          }}
          onChange={(patch) => setFormData((prevState) => new RegisterModel({ ...prevState, ...patch }))}
          extraField={(
            <Field
              label="Email"
              htmlFor="reg-email"
              hint="На неё придёт код подтверждения"
              error={emailError ? (
                <>
                  {emailError}{' '}
                  <Link to="/login" className="form-page__hint-link">Войти</Link>
                  {' · '}
                  <Link to="/forgot-password" className="form-page__hint-link">Забыли пароль?</Link>
                </>
              ) : undefined}
            >
              <input
                id="reg-email"
                ref={emailInputRef}
                className={`form-page__input${emailError ? ' form-page__input--invalid' : ''}`}
                type="email"
                value={formData.email}
                onChange={(e) => {
                  setEmailError('');
                  const email = e.target.value;
                  setFormData((prevState) => new RegisterModel({ ...prevState, email }));
                }}
                autoComplete="email"
                placeholder="name@example.com"
                aria-invalid={!!emailError}
                aria-describedby={emailError ? 'reg-email-error' : undefined}
                required
              />
            </Field>
          )}
        />

        {/* Пароль и подтверждение; требования к паролю — внутри этой же строки, под полями */}
        <div className="form-page__row form-page__row--two">
          <PasswordInput
            id="reg-password"
            label="Пароль"
            value={formData.password}
            onChange={(password) => setFormData((prevState) => new RegisterModel({ ...prevState, password }))}
            autoComplete="new-password"
          />
          <PasswordInput
            id="reg-confirm"
            label="Подтвердите пароль"
            value={confirmPassword}
            onChange={setConfirmPassword}
            autoComplete="new-password"
            invalid={passwordMismatch}
            error={passwordMismatch ? 'Пароли не совпадают' : undefined}
          />
          <PasswordRequirements password={formData.password} />
        </div>

        {/* Роль */}
        <ChipGroup
          label="Роль"
          type="checkbox"
          name="reg-roles"
          loading={rolesLoading}
          emptyText="Список ролей недоступен"
          options={roles
            .filter((role) => role.name !== 'Тренер' || coachInviteValid)
            .map((role) => ({
            value: role.id,
            label: role.name === 'Представитель' ? 'Родитель / представитель' : role.name,
          }))}
          hint="Родитель сможет добавить детей, подавать за них заявки и следить за турнирами"
          error={roleError || undefined}
          selected={formData.roleIds ?? []}
          onToggle={(roleId, checked) => { setRoleError(''); setFormData((prevState) => new RegisterModel({
            ...prevState,
            roleIds: checked
              ? [...(prevState.roleIds ?? []), Number(roleId)]
              : (prevState.roleIds ?? []).filter((id) => id !== Number(roleId)),
          })); }}
        />

        <div className="form-page__consents">
          <label className={`form-page__check${consentError && !consent ? ' form-page__check--invalid' : ''}`}>
            <input
              className="form-page__check-input"
              type="checkbox"
              checked={consent}
              onChange={(e) => { setConsent(e.target.checked); setConsentError(''); }}
            />
            <span className="form-page__check-box"><CheckIcon /></span>
            <span>
              Я даю согласие на обработку моих персональных данных на условиях{' '}
              <Link to="/privacy" target="_blank" rel="noopener noreferrer">Политики конфиденциальности</Link>{' '}
              и принимаю <Link to="/terms" target="_blank" rel="noopener noreferrer">Пользовательское соглашение</Link>
            </span>
          </label>
          {isMinor && (
            <label className={`form-page__check${consentError && !guardianConsent ? ' form-page__check--invalid' : ''}`}>
              <input
                className="form-page__check-input"
                type="checkbox"
                checked={guardianConsent}
                onChange={(e) => { setGuardianConsent(e.target.checked); setConsentError(''); }}
              />
              <span className="form-page__check-box"><CheckIcon /></span>
              <span>
                Мне ещё нет 18 лет. Подтверждаю, что родитель или законный представитель согласен
                на регистрацию и обработку моих персональных данных
              </span>
            </label>
          )}
          {consentError && <span className="form-page__hint form-page__hint--error" role="alert">{consentError}</span>}
        </div>

        <div className="form-page__actions">
          <Link to="/login" className="form-page__login-link">
            Уже есть аккаунт? <span>Войти</span>
          </Link>
          <button type="submit" className="btn form-page__submit" disabled={submitting}>
            {submitting ? 'Отправляем код…' : 'Продолжить'}
          </button>
        </div>
      </form>
      )}

      {step === 'code' && (
        <form className="form-page__form form-page__verify" onSubmit={handleConfirm}>
          <div className="form-page__verify-icon" aria-hidden="true">
            <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="5" width="18" height="14" rx="2.5" />
              <path d="M3.5 7l8.5 6 8.5-6" />
            </svg>
          </div>
          <h3 className="form-page__verify-title">Введите код из письма</h3>
          <p className="form-page__verify-text">
            Мы отправили {REGISTRATION_CODE_LENGTH}-значный код на<br />
            <strong>{formData.email.trim()}</strong>
          </p>

          {codeError && <div className="form-page__alert" role="alert">{codeError}</div>}
          {codeInfo && !codeError && <div className="form-page__notice" role="status">{codeInfo}</div>}

          <input
            ref={codeInputRef}
            className={`form-page__input form-page__code${codeError ? ' form-page__input--invalid' : ''}`}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            autoComplete="one-time-code"
            maxLength={REGISTRATION_CODE_LENGTH}
            value={code}
            onChange={(e) => {
              setCode(e.target.value.replace(/\D/g, '').slice(0, REGISTRATION_CODE_LENGTH));
              setCodeError('');
            }}
            placeholder="······"
            aria-label="Код подтверждения"
          />

          <button
            type="submit"
            className="btn form-page__submit form-page__verify-submit"
            disabled={verifying || code.length !== REGISTRATION_CODE_LENGTH}
          >
            {verifying ? 'Проверяем…' : 'Подтвердить и завершить'}
          </button>

          <div className="form-page__verify-links">
            <button
              type="button"
              className="form-page__link-button"
              onClick={handleResend}
              disabled={resending || resendIn > 0}
            >
              {resendIn > 0 ? `Отправить код ещё раз через ${resendIn} с` : 'Отправить код ещё раз'}
            </button>
            <button type="button" className="form-page__link-button" onClick={() => backToForm()}>
              Изменить email или данные
            </button>
          </div>
          <p className="form-page__hint form-page__verify-spam">Письма нет? Проверьте папку «Спам».</p>
        </form>
      )}
    </FormPage>
  );
};

export default Register;
